import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Typography,
	Box,
	IconButton,
	Stack,
	Grid,
	Divider,
	FormControl,
	FormLabel,
	useTheme,
	useMediaQuery,
	Select,
	Button,
	FormHelperText,
	Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import { outStandingDaysObj } from '@/utils/constants';
import SearchField from '@/components/SearchField';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import InfiniteTablePagination from '../InfiniteTablePagination';
import { addDays, isAfter } from 'date-fns';
import { setSelected } from '@/features/table/dealsTableSlice';
import { chunk } from 'lodash';
import { useLocation, useParams } from 'react-router-dom';
import { OwnerAutocomplete } from '@/components/selects/OwnerSelect';
import Fuse from 'fuse.js';
import { getComparator, stableSort } from '@/components/table/functions';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
	CLIENT_PAGE_SIZE,
	clientPolicyTransactionsColumns,
	columns as dataColumns,
	FUSE_OPTIONS,
	selectPolicyTransactionColumns,
} from '../DataGridColumns/data.js';
import { NoRowsErrorOverlay, NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import { useSearchInsightPolicyTransactionsInfiniteQuery } from '@/features/policies/policiesApi';
import InvoiceTotalsHeader from './InvoiceTotalHeader';
import { FundingStatusSelect } from '@/components/selects/FundingStatusSelect';
import { OutstandingDateFilterSelect } from '@/components/selects/OutstandingDateSelect';
import { FILTER_TYPES } from '@/features/policies/data';
import {
	setEffectiveFromDate,
	setEffectiveToDate,
	setAsAtDate,
	setFundingStatus,
	setOwnerIds,
	setOutstandingDate,
	setTransactionFilter,
	resetFilters,
} from '@/features/table/debtorsTableSlice';
import { LatestPolicyTransactionSelect } from '@/components/selects/LatestPolicyTransactionSelect';
import { parseToInsightDateString, safelyParseDate } from '@/utils/date';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext.jsx';
import DataGridColumnMenu from '../../table/DataGridComponents/DataGridColumnMenu';
import DataGridColumnReorderToolbar from '@/components/table/DataGridComponents/DataGridColumnReorderToolbar';
import {
	CLEAR_FILTER_FLEX,
	GENERAL_FLEX,
	getGridItemProps,
	SEARCH_FLEX,
	useGridItemProps,
} from '@/components/table/filterGrid';
import { FilterAltOffOutlined } from '@mui/icons-material';

const InsightDebtorsTable = ({
	clientId = null,
	displayCheckBoxes = true,
	columnType = 'list',
	displayTransactionFilter = false,
	displayOnlyPosted = false,
	sortByEffectiveDate = false,
	hideTransactionLabel = false,
	transactionLabelDirection = 'column',
	disableColumnWidth = false,
	disableColumnMenu = columnType === 'transaction',
	...props
}) => {
	return (
		<ColumnControlContextProvider value={columnType} variant='debtor'>
			<InsightDebtorsTableWrapper
				{...{
					clientId,
					displayCheckBoxes,
					columnType,
					displayTransactionFilter,
					displayOnlyPosted,
					sortByEffectiveDate,
					hideTransactionLabel,
					transactionLabelDirection,
					disableColumnWidth,
					disableColumnMenu,
					...props,
				}}
			/>
		</ColumnControlContextProvider>
	);
};

const InsightDebtorsTableFilter = ({
	columnType = 'list',
	displayTransactionFilter = false,
	hideTransactionLabel = false,
	transactionLabelDirection = 'column',
	isInitialLoading,
	searchParams = {},
}) => {
	const {
		searchText,
		searchValue,
		setSearchText,
		setSearchValue,
		setPaginationModel,
		disabled: searchDisabled,
	} = searchParams;

	const { searchGridProps, generalGridProps, clearFilterGridProps } = useGridItemProps();

	const dispatch = useDispatch();
	const isDebtorPage = columnType === 'list';

	const filters = useSelector((state) => state.debtorsFilterPrefs);

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const canViewGroup = userDetails?.canViewGroup;

	const handleReset = () => {
		setSearchText('');
		setSearchValue('');
	};

	const handleSearch = () => {
		if (searchText != searchValue) {
			setPaginationModel((prev) => ({ ...prev, page: 0 }));
			setSearchValue(searchText);
		}
	};

	const calculateEffectiveRange = (asAt, range) => {
		const asAtDate = safelyParseDate(asAt);
		if (!asAtDate || !range) return { from: null, to: asAtDate };
		const [addedFrom, addedTo] = outStandingDaysObj[range];
		return {
			from: addedFrom != null ? addDays(asAtDate, -addedFrom) : null,
			to: addedTo != null ? addDays(asAtDate, -addedTo) : null,
		};
	};

	const updateEffectiveRange = (asAt, range) => {
		const { from, to } = calculateEffectiveRange(asAt, range);
		dispatch(setEffectiveFromDate(from?.toISOString() ?? null));
		dispatch(setEffectiveToDate(to?.toISOString() ?? null));
		dispatch(setOutstandingDate(range));
	};

	const handleOutstandingDaysChange = (selectedRange) => {
		const asAt = safelyParseDate(filters.asAtDate) ?? new Date();
		if (!selectedRange) {
			dispatch(setEffectiveFromDate(null));
			dispatch(setOutstandingDate(null));
			dispatch(setEffectiveToDate(asAt));
		} else {
			updateEffectiveRange(asAt, selectedRange);
		}
	};

	const handleAsAtChange = (value) => {
		if (!value) return;
		dispatch(setAsAtDate(value));

		const outstandingRange = filters.outstandingDate;
		if (outstandingRange) {
			updateEffectiveRange(value, outstandingRange);
		} else {
			dispatch(setEffectiveFromDate(null));
			dispatch(setEffectiveToDate(value));
		}
	};

	const showSearchBar = columnType === 'client' || columnType === 'list';

	return (
		<Grid
			container
			spacing={1}
			sx={{
				width: '100%',
				pb: 2,
			}}
		>
			<Grid container spacing={2} width='100%'>
				{showSearchBar && (
					<Grid {...searchGridProps}>
						<FormControl fullWidth>
							<FormLabel>Search</FormLabel>
							<SearchField
								value={searchText}
								disabled={searchDisabled}
								onKeyDown={(e) => {
									if (e.key === 'Enter') handleSearch();
								}}
								onChange={(e) => setSearchText(e.target.value)}
								onClick={handleSearch}
								onReset={handleReset}
							/>
						</FormControl>
					</Grid>
				)}

				{/* Transaction Filter - Second, always visible when displayTransactionFilter is true */}
				{displayTransactionFilter && (
					<Grid {...generalGridProps}>
						<Stack
							direction={transactionLabelDirection}
							{...(transactionLabelDirection === 'row'
								? {
										sx: { alignItems: 'center' },
										spacing: 2,
									}
								: { width: '100%' })}
						>
							{!hideTransactionLabel && (
								<FormLabel>{`${transactionLabelDirection === 'row' ? 'Policy Transactions' : 'Transaction'}`}</FormLabel>
							)}
							<LatestPolicyTransactionSelect
								status={filters.transactionFilter}
								disabled={isInitialLoading || !userDetails?.canViewGroup}
								onChange={(newStatus) => {
									dispatch(setTransactionFilter(newStatus));
								}}
							/>
							{showSearchBar && <FormHelperText>&nbsp;</FormHelperText>}
						</Stack>
					</Grid>
				)}

				{/* Other filters - Only when columnType is client or list */}
				{(columnType === 'client' || columnType === 'list') && (
					<>
						{canViewGroup && isDebtorPage && (
							<Grid {...generalGridProps}>
								<FormControl fullWidth>
									<FormLabel>Client Owner</FormLabel>
									<OwnerAutocomplete includeInactive
										multiple
										userId={filters.ownerIds}
										disabled={isInitialLoading || !userDetails?.canViewGroup}
										onChange={(newUsers) => {
											dispatch(setOwnerIds(newUsers));
										}}
										returnValue='id'
										disableClearable={true}
									/>
									{showSearchBar && <FormHelperText>&nbsp;</FormHelperText>}
								</FormControl>
							</Grid>
						)}

						{isDebtorPage && (
							<>
								<Grid {...generalGridProps}>
									<FormControl fullWidth>
										<FormLabel htmlFor='asAt'>As at</FormLabel>
										<LocalizationProvider dateAdapter={AdapterDateFns}>
											<DatePicker
												name='asAt'
												disabled={isInitialLoading}
												format='dd/MM/yyyy'
												value={
													filters.asAtDate
														? new Date(filters.asAtDate)
														: new Date()
												}
												slotProps={{
													textField: {
														id: 'endDateTo',
														sx: { '& input': { paddingY: '7.5px' } },
													},
												}}
												onChange={handleAsAtChange}
											/>
										</LocalizationProvider>
										{showSearchBar && <FormHelperText>&nbsp;</FormHelperText>}
									</FormControl>
								</Grid>

								<Grid {...generalGridProps}>
									<FormControl fullWidth>
										<FormLabel>Outstanding days</FormLabel>
										<OutstandingDateFilterSelect
											outstandingDate={filters.outstandingDate}
											onChange={handleOutstandingDaysChange}
											disabled={isInitialLoading}
										/>
										{showSearchBar && <FormHelperText>&nbsp;</FormHelperText>}
									</FormControl>
								</Grid>

								<Grid {...generalGridProps}>
									<Stack direction='column' width='100%'>
										<FundingStatusSelect
											fundingStatus={filters.fundingStatus}
											onChange={(newValue) =>
												dispatch(setFundingStatus(newValue))
											}
											disabled={isInitialLoading}
										/>
										{showSearchBar && <FormHelperText>&nbsp;</FormHelperText>}
									</Stack>
								</Grid>

								<Grid {...clearFilterGridProps}>
									<Tooltip title='Clear filters'>
										<IconButton
											sx={{
												...(showSearchBar && { mb: 3 }),
											}}
											// sx={{ width: '100%', minHeight: '40px', borderRadius: '6px' }}
											variant='text'
											size='small'
											color='primary'
											onClick={() => dispatch(resetFilters())}
										>
											<FilterAltOffOutlined />
										</IconButton>
									</Tooltip>
								</Grid>
							</>
						)}
					</>
				)}
			</Grid>
		</Grid>
	);
};

const InsightDebtorsTableWrapper = ({
	clientId = null,
	displayCheckBoxes = true,
	columnType = 'list',
	displayTransactionFilter = false,
	displayOnlyPosted = false,
	sortByEffectiveDate = false,
	hideTransactionLabel = false,
	transactionLabelDirection = 'column',
	disableColumnWidth = false,
	disableColumnMenu = columnType === 'transaction',
	...props
}) => {
	const sx = props?.sx ?? {};
	const theme = useTheme();

	const dispatch = useDispatch();

	const apiRef = useGridApiRef();
	const elementRef = useRef(null);

	const isDebtorPage = columnType === 'list';

	const filters = useSelector((state) => state.debtorsFilterPrefs);

	const [searchText, setSearchText] = useState('');
	const [searchValue, setSearchValue] = useState('');
	const [refreshIndicator, setRefreshIndicator] = useState(0);

	const { selected } = useSelector((state) => state.dealTable);

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails, ...userQuery } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const [paginationModel, setPaginationModel] = useState({
		pageSize: columnType === 'list' ? CLIENT_PAGE_SIZE : 10,
		page: 0,
	});

	const [sortModel, setSortModel] = useState([
		{
			field: 'Date',
			sort: 'asc',
		},
	]);

	const [hoveredDealId, setHoveredDealId] = useState(null);
	//Default as at to current date
	// these filters don't get used in the client screen so might be safe
	// to just remove this
	// useEffect(() => {
	// 	if (!isDebtorPage) {
	// 		dispatch(setEffectiveToDate(new Date().toISOString()));
	// 		dispatch(setAsAtDate(new Date().toISOString()));
	// 		dispatch(setOutstandingDate(null));
	// 	}
	// }, []);

	const { data, fetchNextPage, isFetchingNextPage, hasNextPage, ...infiniteQuery } =
		useSearchInsightPolicyTransactionsInfiniteQuery(
			{
				...(isDebtorPage && {
					ownerDocIds: filters.ownerIds,
					effectiveFromDate: parseToInsightDateString(filters.effectiveFromDate),
					effectiveToDate: parseToInsightDateString(filters.effectiveToDate),
					fundingStatus: filters.fundingStatus,
				}),
				...(!userDetails?.canViewGroup && { authRepId: userDetails?.authorisedRepId }),
				displayOnlyPosted: displayOnlyPosted,
				...(clientId && { clientId: clientId }),
				filterBy: displayTransactionFilter
					? (filters.transactionFilter ?? FILTER_TYPES?.ShowLatest)
					: FILTER_TYPES.ShowOutstanding,
			},
			{ skip: !userDetails || userQuery.isFetching },
		);

	const sortByPolicyEffectiveDateAndTranId = () => (a, b) => {
		if (!sortByEffectiveDate) {
			return 0;
		}

		const dateA = safelyParseDate(a.PolicyEffectiveDate)?.getTime?.() ?? 0;
		const dateB = safelyParseDate(b.PolicyEffectiveDate)?.getTime?.() ?? 0;
		if (dateA !== dateB) {
			return dateB - dateA;
		}
		const invA = a.InvoiceNumber;
		const invB = b.InvoiceNumber;
		return invB - invA;
	};

	const allPageData = [...(data?.pages.flatMap((r) => r.Results ?? []) ?? [])]
		.map((r) => ({
			...r,
			...(r.ClientDocId && { clientDocId: r.ClientDocId }),
			...(r.OwnerDocId && { ownerDocId: r.OwnerDocId }),
		}))
		.sort(sortByPolicyEffectiveDateAndTranId());

	const invoicesIndex = useMemo(() => {
		if (allPageData.length > 0) {
			const fuseIndex = Fuse.createIndex(FUSE_OPTIONS.keys, allPageData);
			return new Fuse(allPageData, FUSE_OPTIONS, fuseIndex);
		}
	}, [allPageData]);

	const searchedPages = useMemo(() => {
		return (invoicesIndex?.search(searchValue) ?? []).flatMap((v) => v?.item);
	}, [invoicesIndex, searchValue]);

	const allPages = stableSort(
		(searchValue ? searchedPages : allPageData) ?? [],

		getComparator(sortModel?.[0]?.sort, sortModel?.[0]?.field, true),
	);

	const pageChunks = chunk(allPages ?? [], paginationModel.pageSize);

	const totalRecordCount = data?.pages?.[0]?.RecordCount;

	const count = useMemo(() => {
		if (searchValue.length > 0) {
			return searchedPages.length;
		}
		if (filters.ownerIds?.length > 0) {
			return allPages.length;
		}
		if (totalRecordCount != null && hasNextPage) {
			return totalRecordCount;
		}
		return allPageData.length;
	}, [
		searchValue,
		filters.ownerIds,
		searchedPages.length,
		allPages.length,
		allPageData.length,
		totalRecordCount,
		hasNextPage,
	]);

	const displayedRows = pageChunks[paginationModel.page] ?? [];

	useEffect(() => {
		let cancelled = false;

		const fetchServerPages = async () => {
			if (
				infiniteQuery.isUninitialized ||
				!data?.pages?.length ||
				!hasNextPage ||
				infiniteQuery.isFetching ||
				cancelled
			) {
				return;
			}

			await fetchNextPage();
		};

		fetchServerPages();

		return () => {
			cancelled = true;
		};
	}, [data?.pages?.length, hasNextPage, infiniteQuery.isFetching, infiniteQuery.isUninitialized]);

	const isLoadingResults =
		infiniteQuery.isUninitialized ||
		infiniteQuery.isLoading ||
		(infiniteQuery.isFetching && !isFetchingNextPage);
	const isInitialLoading = infiniteQuery.isUninitialized || infiniteQuery.isLoading;

	useEffect(() => {
		setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
	}, [filters]);

	const handlePaginationModelChange = (newModel) => {
		setPaginationModel((prev) => ({ ...prev, page: newModel?.page }));
	};

	const handleSortModelChange = async (newModel) => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
		setSortModel(newModel);
	};

	useEffect(() => {
		if (!infiniteQuery.isUninitialized && data) {
			infiniteQuery.refetch();
			if (paginationModel.page != 0) {
				setPaginationModel((prev) => ({ ...prev, page: 0 }));
			}
		}
		if (selected.length > 0) {
			dispatch(setSelected([]));
		}
	}, [refreshIndicator]);

	const rowSelectionModel = selected.map((policy) => policy.Id);

	const TablePagination = useCallback(() => {
		const totalClientPages = Math.ceil(allPages.length / paginationModel.pageSize);

		return (
			<InfiniteTablePagination
				{...{
					count,
					page: paginationModel.page,
					setPage: (page) => handlePaginationModelChange({ page }),
					rowsPerPage: paginationModel.pageSize,
					hasNextPage: paginationModel.page + 1 < totalClientPages,
					hasPreviousPage: paginationModel.page > 0,
				}}
			/>
		);
	}, [count, allPages, paginationModel.pageSize, paginationModel.page]);

	const dataGridKey = JSON.stringify({
		page: paginationModel.page,
		sort: sortModel,
		query: filters,
	});

	const columns = useMemo(() => {
		let data = dataColumns;
		if (columnType === 'transaction') {
			data = selectPolicyTransactionColumns;
		}
		if (columnType === 'client') {
			data = clientPolicyTransactionsColumns;
		}
		return data.map((c) => ({ ...c, disableColumnMenu: !c.hideable }));
	}, [columnType]);

	const { columns: gridColumns, ...gridColumnProps } = useDataGridColumnProps(columns, {
		disableColumnWidth,
		disableColumnVisibility: disableColumnMenu,
		disableColumnOrder: disableColumnMenu,
	});

	return (
		<Box sx={{ width: '100%', ...sx }}>
			<InsightDebtorsTableFilter
				{...{
					columnType,
					displayTransactionFilter,
					hideTransactionLabel,
					transactionLabelDirection,
					isInitialLoading,
				}}
				searchParams={{
					searchText,
					setSearchText,
					searchValue,
					setSearchValue,
					disabled: isInitialLoading || rowSelectionModel.length > 0,
				}}
			/>
			<Box
				className='MuiDataGrid-parentDiv'
				sx={{
					display: 'flex',
					flexDirection: 'column',
					width: '100%',
					minWidth: 0,
					minHeight: '8em',
					maxHeight: 'calc(100vh - 190px)',
				}}
			>
				<DataGrid
					key={dataGridKey}
					apiRef={apiRef}
					ref={elementRef}
					loading={isLoadingResults}
					width='100%'
					getRowHeight={() => 'auto'}
					getRowId={(row) => String(row?.Id)}
					getRowClassName={(params) => {
						const { row } = params;
						return `row-invoice-id-${row?.Id}${
							hoveredDealId === row?.Id ? ' hovered-row' : ''
						}`;
					}}
					initialState={{
						sorting: { sortModel },
					}}
					sortingOrder={['desc', 'asc']}
					sx={{
						backgroundColor: theme.palette.background.paper,
						'& .MuiDataGrid-editInputCell': {
							backgroundColor: 'transparent',
						},
						'--DataGrid-overlayHeight': isLoadingResults ? '5em' : '3em',
						'& .MuiDataGrid-cell': {
							padding: 1,
							minHeight: '42px',
						},
						'& .MuiDataGrid-cell[data-field="actions"]': {
							position: 'sticky',
							right: 0,
							zIndex: 2,
							padding: 0,
							backgroundColor: theme.palette.background.paper,
							boxShadow: 'none',
							borderRight: 'none !important',
							borderLeft: 'none !important',
						},
						'& .MuiDataGrid-columnHeader[data-field="actions"]': {
							position: 'sticky',
							right: 0,
							zIndex: 3,
							padding: 0,
							resize: 'none',
							backgroundColor: theme.palette.background.paper,
							borderRight: 'none !important',
							borderLeft: 'none !important',
						},
						'& .MuiDataGrid-columnHeader[data-field="actions"] .MuiDataGrid-columnSeparator':
							{
								display: 'none',
								visibility: 'hidden',
							},
						'& .MuiDataGrid-virtualScroller': {
							overflowX: 'auto !important',
						},
						border: 'none',

						minHeight: '6em',
						height: isLoadingResults ? '6em' : 'auto',
						'& .MuiDataGrid-overlayWrapperInner': {
							minHeight: '5em',
						},
					}}
					rows={isLoadingResults ? [] : displayedRows}
					columns={gridColumns}
					rowSelectionModel={rowSelectionModel}
					checkboxSelection={displayCheckBoxes}
					disableRowSelectionOnClick
					disableSelectionOnClick
					disableDensitySelector
					{...gridColumnProps}
					{...(disableColumnMenu && { disableColumnMenu: true })}
					slots={{
						noRowsOverlay: infiniteQuery.isError
							? () => <NoRowsErrorOverlay objectType='invoices' />
							: () => <NoRowsOverlay objectType='invoices' />,
						pagination: TablePagination,
						...(!disableColumnMenu && {
							toolbar: DataGridColumnReorderToolbar,
							columnMenu: DataGridColumnMenu,
						}),
					}}
					slotProps={{
						loadingOverlay: {
							variant: 'skeleton',
							noRowsVariant: 'skeleton',
						},
					}}
					paginationMode='server'
					filterMode='server'
					sortingMode='server'
					rowCount={count}
					paginationModel={paginationModel}
					sortModel={sortModel}
					onSortModelChange={handleSortModelChange}
					{...(props?.dataGrid ?? {})}
				/>
			</Box>
		</Box>
	);
};
export default InsightDebtorsTable;
